import axios from 'axios'

import usePlugin from '@/composables/plugin'
import useStore from '@/composables/store'

export default function useStoreUrls() {
  const { t } = useI18n()
  const notification = useNotification()
  const { getAddressFromPlaceId } = usePlugin()
  const { updateStores } = useStore()

  const isProcessing = ref(false)

  const saveGooglePlaceId = async (
    storeId: number,
    googlePlaceId: any,
    currentGooglePlaceId?: string | null,
    showNotification = true
  ) => {
    if (isProcessing.value) return false
    isProcessing.value = true

    try {
      const placeIdString = typeof googlePlaceId === 'string' ? googlePlaceId : googlePlaceId?.place_id
      const placeIdToUse = placeIdString ?? currentGooglePlaceId

      const storeAddress = await getAddressFromPlaceId(placeIdToUse)

      const requests = [axios.patch(`stores/${storeId}`, { storeAddress })]

      if (placeIdString) {
        requests.push(
          axios.patch(`stores/${storeId}/google-place-id`, {
            googlePlaceId: placeIdString
          })
        )
      }

      await Promise.all(requests)
      await updateStores()

      if (showNotification) {
        notification.create('success', {
          text: t('pages.settings.storeDetailPage.notifications.update.success')
        })
      }

      return true
    } catch {
      if (showNotification) {
        notification.create('error', {
          text: t('pages.settings.storeDetailPage.notifications.update.error')
        })
      }
      return false
    } finally {
      isProcessing.value = false
    }
  }

  const saveMultipleGooglePlaceIds = async (
    stores: Array<{ id: number; googlePlaceId: any; currentGooglePlaceId?: string | null }>
  ) => {
    if (isProcessing.value) return false
    isProcessing.value = true

    try {
      const promises = stores.map(async (store) => {
        try {
          const placeIdString =
            typeof store.googlePlaceId === 'string' ? store.googlePlaceId : store.googlePlaceId?.place_id
          const placeIdToUse = placeIdString ?? store.currentGooglePlaceId

          const storeAddress = await getAddressFromPlaceId(placeIdToUse)

          const requests = [axios.patch(`stores/${store.id}`, { storeAddress })]

          if (placeIdString) {
            requests.push(
              axios.patch(`stores/${store.id}/google-place-id`, {
                googlePlaceId: placeIdString
              })
            )
          }

          await Promise.all(requests)
          return true
        } catch {
          return false
        }
      })

      const results = await Promise.all(promises)
      await updateStores()

      const allSuccess = results.every((result) => result === true)

      if (allSuccess) {
        notification.create('success', {
          text: t('pages.settings.storeDetailPage.notifications.update.success')
        })
      } else {
        notification.create('error', {
          text: t('pages.settings.storeDetailPage.notifications.update.error')
        })
      }

      return allSuccess
    } catch {
      notification.create('error', {
        text: t('pages.settings.storeDetailPage.notifications.update.error')
      })
      return false
    } finally {
      isProcessing.value = false
    }
  }

  const saveMultipleStoreUrls = async (stores: Array<{ id: number; type: 'facebook' | 'instagram'; url: string }>) => {
    if (isProcessing.value) return false
    isProcessing.value = true
    try {
      const promises = stores.map(async (store) => {
        try {
          const urls = [
            {
              storeUrlCategorySlug: store.type,
              url: store.url
            }
          ]

          await axios.put(`stores/${store.id}/urls`, { urls })
          return true
        } catch {
          return false
        }
      })

      const results = await Promise.all(promises)
      await updateStores()

      const allSuccess = results.every((result) => result === true)

      if (allSuccess) {
        notification.create('success', {
          text: t('pages.settings.storeDetailPage.notifications.update.success')
        })
      } else {
        notification.create('error', {
          text: t('pages.settings.storeDetailPage.notifications.update.error')
        })
      }

      return allSuccess
    } catch {
      notification.create('error', {
        text: t('pages.settings.storeDetailPage.notifications.update.error')
      })
      return false
    } finally {
      isProcessing.value = false
    }
  }

  return {
    isProcessing,
    saveGooglePlaceId,
    saveMultipleGooglePlaceIds,
    saveMultipleStoreUrls
  }
}
